<template>
  <v-dialog
    persistent
    fullscreen
    lazy
    v-model="opened"
    @keydown.esc="closeDialog"
  >
    <!-- State: Loading -->
    <v-card
      v-if="taxReturn === null || currentCompany === null"
      class="taxReturnDeliveryDialog taxReturnDeliveryDialog--loading"
    >
      <sc-flex :is-column="true" :gap="16" :is-y-centered="true">
        <v-progress-circular indeterminate size="64" color="primary" />
        <sc-small> Loading tax return... </sc-small>
        <sc-button-group align="center">
          <sc-button @click="closeDialog" type="secondary" size="small"
            >Cancel</sc-button
          >
        </sc-button-group>
      </sc-flex>
    </v-card>

    <!-- State: Editing -->
    <v-card v-else flat class="taxReturnDeliveryDialog">
      <div class="taxReturnDeliveryDialog__header">
        <!-- Name -->
        <sc-flex class="taxReturnDeliveryDialog__name" :gap="8">
          <!-- Name: Editing state -->
          <sc-flex v-if="editingTaxReturnName" :is-y-centered="true" :gap="8">
            <sc-flex :is-y-centered="true" :gap="2">
              <input v-model="newTaxReturnName" autofocus="true" />
              <sc-text>.pdf</sc-text>
            </sc-flex>
            <sc-button-group>
              <sc-button
                type="primary"
                @click="saveEditTaxReturnName"
                size="small"
                icon-left="check"
                :is-icon-only="true"
              ></sc-button>
              <sc-button
                type="secondary"
                size="small"
                @click="cancelEditTaxReturnName"
                :is-icon-only="true"
                icon-left="close"
              ></sc-button>
            </sc-button-group>
          </sc-flex>
          <!-- Name: Viewing state -->
          <sc-flex v-else :is-y-centered="true" :gap="8">
            <sc-text class="taxReturnDeliveryDialog__name__view">
              {{ taxReturn.tax_return_name }}
            </sc-text>
            <sc-button-group>
              <sc-button
                type="secondary"
                size="small"
                @click="editTaxReturnName()"
                icon-left="edit"
                :is-icon-only="true"
              >
              </sc-button>
            </sc-button-group>
          </sc-flex>
        </sc-flex>

        <!-- Controls -->
        <sc-flex :is-y-centered="true" :is-between="true" :is-full-width="true">
          <!-- Tabs -->
          <v-tabs class="tabs" v-model="activeTab" slider-color="info">
            <v-tab
              v-for="(step, index) in steps"
              class="v-tab-item"
              :key="step.key"
              :href="`#${step.key}`"
              :disabled="
                step.disabled || (!readonly && index > currentStepIndex)
              "
            >
              <span>{{ index + 1 }}.</span>
              <span>{{ step.name }}</span>
            </v-tab>
          </v-tabs>

          <!-- Close button -->
          <sc-button-group>
            <sc-button
              type="secondary"
              @click="closeDialog"
              :is-round="true"
              icon-left="close"
              size="small"
            ></sc-button>
          </sc-button-group>
        </sc-flex>
      </div>

      <!-- Content -->
      <div class="tabs-content-container">
        <v-tabs-items v-model="activeTab">
          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="classifications"
            v-if="activeTab === 'classifications'"
          >
            <classifications
              :current-company="currentCompany"
              :tax-return="taxReturn"
              @classification-updated="
                taxReturn => onReturnUpdated(taxReturn, true)
              "
              @next="next"
            />
          </v-tab-item>

          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="e-sign-forms"
            v-if="activeTab === 'e-sign-forms'"
          >
            <e-sign-forms
              :current-company="currentCompany"
              :tax-return="taxReturn"
              @open-filing-instructions="openFilingInstructions"
              @e-sign-forms-updated="
                taxReturn => onReturnUpdated(taxReturn, true)
              "
              @save-signature="onSaveSignature"
              @next="next"
            />
          </v-tab-item>

          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="paper-filed-returns"
            v-if="activeTab === 'paper-filed-returns'"
          >
            <paper-filed-returns
              :current-company="currentCompany"
              :tax-return="taxReturn"
              @paper-filed-returns-updated="
                taxReturn => onReturnUpdated(taxReturn, true)
              "
              @next="next"
            />
          </v-tab-item>
          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="payment-vouchers"
            v-if="activeTab === 'payment-vouchers'"
          >
            <payment-vouchers
              :current-company="currentCompany"
              :tax-return="taxReturn"
              @payment-vouchers-updated="
                taxReturn => onReturnUpdated(taxReturn, true)
              "
              @open-filing-instructions="openFilingInstructions"
              @next="next"
            />
          </v-tab-item>
          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="k1-distribution"
            v-if="activeTab === 'k1-distribution'"
          >
            <k1-distribution
              :current-company="currentCompany"
              :tax-return="taxReturn"
              @next="next"
            />
          </v-tab-item>

          <v-tab-item
            lazy
            :transition="false"
            :reverse-transition="false"
            value="review-and-deliver"
            v-if="activeTab === 'review-and-deliver'"
          >
            <review-and-deliver
              :current-company="currentCompany"
              :tax-return="taxReturn"
              @return-delivered="onReturnDelivered"
              @return-updated="onReturnUpdated"
            />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import { mapGetters } from 'vuex';
import Classifications from './Classifications.vue';
import ESignForms from './ESignForms.vue';
import PaperFiledReturns from './PaperFiledReturns.vue';
import PaymentVouchers from './PaymentVouchers.vue';
import K1Distribution from './K1Distribution.vue';
import ReviewAndDeliver from './ReviewAndDeliver.vue';
import axios from 'axios';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

export default {
  components: {
    Classifications,
    ESignForms,
    PaperFiledReturns,
    PaymentVouchers,
    K1Distribution,
    ReviewAndDeliver,
  },

  mixins: [FirmPreferenceMixins],

  props: {
    opened: {
      type: Boolean,
      default: false,
    },

    currentCompanyId: {
      type: Number,
      default: null,
    },

    taxReturnId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      currentCompany: null,

      // Tax return state
      taxReturn: null,
      filingInstructionsWindow: null,
      editingTaxReturnName: false,
      newTaxReturnName: '',

      // Tab state
      activeTab: 'classifications',
    };
  },

  methods: {
    editTaxReturnName() {
      this.newTaxReturnName = this.taxReturnNameWithoutExtension;
      this.editingTaxReturnName = true;
    },

    cancelEditTaxReturnName() {
      this.editingTaxReturnName = false;
    },

    async getCurrentCompany() {
      const response = await API.company.getCompany(this.currentCompanyId);
      this.currentCompany = response.data;
    },

    async getTaxReturn() {
      const response = await axios.get(`/tax_returns/${this.taxReturnId}`, {
        params: {
          company_id: this.currentCompanyId,
        },
      });

      this.taxReturn = response.data;
    },

    async saveEditTaxReturnName() {
      const response = await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.currentCompany.id,
        tax_return_name: this.newTaxReturnName + '.pdf',
      });

      this.$store.dispatch('application/setSuccessNotification', {
        message: `Saved successfully!`,
      });

      this.taxReturn.tax_return_name = response.data.tax_return_name;
      this.editingTaxReturnName = false;
      this.$emit('refresh-table');
    },

    next() {
      const nextStep = this.steps.find((step, index) => {
        return index > this.currentStepIndex && !step.disabled;
      });

      this.activeTab = nextStep.key;
    },

    onReturnDelivered() {
      this.$emit('return-delivered', this.taxReturn.id);
      this.closeDialog();
    },

    onReturnUpdated(taxReturn, goToNextStep = false) {
      this.taxReturn = taxReturn;
      this.$emit('refresh-table');

      if (goToNextStep) {
        this.next();
      }
    },

    onSaveSignature(taxReturn) {
      this.taxReturn = taxReturn;
      this.$emit('refresh-table');
    },

    closeDialog() {
      // When this dialog is embededed in iframe, we dispatch an event
      // so that the parent window can close it
      if (this.isEmbeddedInNext && this.isInIframe) {
        const event = new CustomEvent('closeDialog');
        window.parent.document.dispatchEvent(event);
      } else {
        this.$emit('update:opened', false);
      }

      // Close the Filing Instructions pop-up window
      if (this.filingInstructionsWindow) {
        this.filingInstructionsWindow.close();
      }
    },

    openFilingInstructions() {
      const url = `/filing-instructions?taxReturnId=${this.taxReturnId}&companyId=${this.taxReturn.company_id}`;

      this.filingInstructionsWindow = window.open(
        url,
        'Filing Instructions',
        'height=1000,width=1000',
      );

      this.filingInstructionsWindow.focus();

      this.filingInstructionsWindow.addEventListener('beforeunload', () => {
        this.filingInstructionsWindow = null;
      });
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentUserFirm: 'currentFirm',
    }),

    taxReturnNameWithoutExtension() {
      return this.taxReturn.tax_return_name.replace(/\.[^/.]+$/, '');
    },

    readonly() {
      return this.taxReturn.status !== 'action_required';
    },

    currentStepIndex() {
      return this.steps.findIndex(step => step.key === this.activeTab);
    },

    steps() {
      if (!this.taxReturn) {
        return [];
      } else {
        const steps = [];

        steps.push({
          key: 'classifications',
          name: 'Classifications',
        });

        steps.push({
          key: 'e-sign-forms',
          name: 'E-sign Forms',
          disabled: this.taxReturn.e_sign_forms.length === 0,
        });

        if (this.taxReturnPaperFiledReturnsEnabled) {
          steps.push({
            key: 'paper-filed-returns',
            name: 'Paper-Filed Returns',
          });
        }

        steps.push({
          key: 'payment-vouchers',
          name: 'Payments',
        });

        if (
          this.taxReturnK1DistributionEnabled &&
          this.currentCompany &&
          this.currentCompany.entity_type !== 'Individual'
        ) {
          steps.push({
            key: 'k1-distribution',
            name: 'K-1 Distribution',
          });
        }

        steps.push({
          key: 'review-and-deliver',
          name: 'Review & Deliver',
        });

        return steps;
      }
    },
  },

  async created() {
    await this.getCurrentCompany();
    await this.getTaxReturn();
  },

  watch: {
    async opened(value) {
      if (value) {
        await this.getTaxReturn();
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.taxReturnDeliveryDialog {
  flex-direction: column;
  display: flex;
  overflow: hidden;
  min-height: calc(100dvh);
  max-height: calc(100dvh);
  width: 100%;

  background-color: var(--gray-0);
  /* TODO @chris Try moving this important to components. */
  text-align: left !important;
}

.taxReturnDeliveryDialog__header {
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 4px;
  justify-content: space-between;
  padding: 0 12px 4px 12px;
  width: 100%;
}

.taxReturnDeliveryDialog__header > * {
  width: 100%;
}

@media (max-width: 1260px) {
  .taxReturnDeliveryDialog__header {
    align-items: flex-start;
    flex-direction: column-reverse;
  }
}

.taxReturnDeliveryDialog--loading {
  align-items: center;
  justify-content: center;
}

.taxReturnDeliveryDialog__name {
  display: flex;
  max-width: 100%;
  width: 350px;
}

@media (max-width: 1260px) {
  .taxReturnDeliveryDialog__name {
    margin: 0 auto;

    width: unset;
  }
}

.taxReturnDeliveryDialog__name input {
  font-family: var(--font-family-sans);
  font-size: 16px;

  border: 0 !important;
}

.taxReturnDeliveryDialog__name input:active,
.taxReturnDeliveryDialog__name input:focus {
  border: 0;
  outline: none;
}

.taxReturnDeliveryDialog__name__view {
  display: -webkit-box;
  overflow: hidden;
  max-width: 100%;

  word-break: break-all;

  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

@media (max-width: 1260px) {
  .taxReturnDeliveryDialog__name__view {
    max-width: calc(100% - 32px);

    -webkit-line-clamp: 1;
  }
}

.dialog-content {
  overflow: hidden;
}

.v-tab-item {
  display: flex !important;
  justify-content: center;
}

.tabs {
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.tabs >>> .v-tabs__container {
  height: 56px;
}

.tabs >>> .v-tabs__item {
  text-transform: none;
  font-weight: bold;
  font-size: 15px;
  padding: 24px 16px;
}

.tabs >>> .v-tabs__slider-wrapper {
  top: 0;
}

.tabs >>> .v-tabs__slider {
  height: 4px;
}

@media (max-width: 960px) {
  .tabs >>> .v-tabs__item {
    text-transform: none;
    font-weight: bold;
    font-size: 16px;
    padding: 16px 12px;
  }
}

@media (max-width: 760px) {
  .v-tab-item span:nth-child(2) {
    display: none;
  }
}

.v-window__container {
  flex: auto;
  min-height: 0;
}

.tab-content-container {
  height: calc(100dvh - 60px);
  width: 100%;
}

@media (max-width: 1260px) {
  .tab-content-container {
    height: calc(100dvh - 96px);
  }
}

.tabs-content-container {
  overflow: hidden;
}
</style>
